import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertTriangle } from "lucide-react";

// Two-step delete: 1) warning, 2) type the name (and password if needed) to confirm.
export function DeleteAccountDialog({ title, warning, items = [], confirmName, needPassword = false, onConfirm, onClose }) {
  const [step, setStep] = useState(1);
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const ok = (!confirmName || name.trim().toLowerCase() === confirmName.trim().toLowerCase()) && (!needPassword || password);

  const go = async () => {
    setBusy(true); setError("");
    try { await onConfirm({ confirm_name: name, password }); }
    catch (e) { setError(e.message || "Something went wrong"); setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md w-[calc(100vw-1.5rem)] rounded-2xl" data-testid="delete-account-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2 text-rose-700"><AlertTriangle className="h-5 w-5" />{title}</DialogTitle></DialogHeader>
        {step === 1 ? (
          <div className="space-y-3 text-sm" data-testid="delete-step-1">
            <p className="text-stone-700">{warning}</p>
            {items.length > 0 && <ul className="list-disc pl-5 text-stone-600 space-y-0.5">{items.map((i) => <li key={i}>{i}</li>)}</ul>}
            <p className="font-semibold text-rose-700">This can't be undone.</p>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" className="rounded-full" onClick={onClose} data-testid="delete-cancel">Keep it</Button>
              <Button className="rounded-full bg-rose-600 hover:bg-rose-700" onClick={() => setStep(2)} data-testid="delete-continue">I understand, continue</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3 text-sm" data-testid="delete-step-2">
            {confirmName && <div><Label>Type <b className="font-mono">{confirmName}</b> to confirm</Label>
              <Input data-testid="delete-confirm-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5 h-11 rounded-lg" autoComplete="off" /></div>}
            {needPassword && <div><Label>Your password</Label>
              <Input type="password" data-testid="delete-confirm-password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5 h-11 rounded-lg" /></div>}
            {error && <p className="text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2" data-testid="delete-error">{error}</p>}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" className="rounded-full" onClick={onClose}>Cancel</Button>
              <Button disabled={!ok || busy} className="rounded-full bg-rose-600 hover:bg-rose-700" onClick={go} data-testid="delete-final-button">{busy ? "Deleting…" : "Delete permanently"}</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
